// Chemin : C:\gestion-copro\src\components\chat\ChatMessage.tsx
// Session 137 : Remplacement complet de react-markdown par un parseur Markdown léger
// Raison : react-markdown (micromark) ne gère pas le gras avec caractères Unicode (€, accents)
// Session 172 : REFONTE PARSEUR — Ajout tableaux markdown, encadrés colorés (⚠️/✅/❌/💡),
//               italique, code inline, meilleur espacement, rendu pro pour calculs SENTINELLE
// Session 175 : Fix séparateur tableau markdown — regex élargie pour couvrir tous les formats (|-|---|, etc.)
// Session 279 : Rendu H4 (####) + bandeau avertissement placeholders [xxx]

import React from 'react';
import { Camera } from 'lucide-react';
import { ChatMessage as ChatMessageType, ProposedAction } from '../../types/chat.types';
import ActionCard from './ActionCard';
import { useChat } from '../../context/ChatContext';

// ============================================================
// Parseur Markdown léger — adapté aux réponses SENTINELLE
// ============================================================

/**
 * Traite le formatage inline : **gras**, *italique*, `code`, [liens](url)
 */
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Regex combinée : gras OU italique OU code inline OU lien
  const regex = /\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Texte avant le match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      // **gras**
      parts.push(<strong key={`b${key++}`} className="font-semibold">{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      // *italique*
      parts.push(<em key={`i${key++}`} className="italic">{match[2]}</em>);
    } else if (match[3] !== undefined) {
      // `code inline`
      parts.push(
        <code key={`c${key++}`} className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-xs font-mono">
          {match[3]}
        </code>
      );
    } else if (match[4] !== undefined && match[5] !== undefined) {
      // [texte](url)
      parts.push(
        <a
          key={`a${key++}`}
          href={match[5]}
          className="text-blue-600 underline hover:text-blue-800"
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[4]}
        </a>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Texte restant après le dernier match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Détecte si une ligne est un séparateur de tableau markdown (|---|---|)
 * Couvre les variantes : |---|---|, | --- | --- |, |:---|---:|, |-|---|, etc.
 */
function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  // La ligne doit commencer par | et ne contenir que |, -, :, espaces
  if (!trimmed.startsWith('|')) return false;
  // Retirer les pipes et espaces — il ne doit rester que des tirets et deux-points
  const inner = trimmed.replace(/\|/g, '').replace(/[\s:-]/g, '');
  // Si vide après nettoyage = c'est un séparateur (il n'y avait que des |, -, :, espaces)
  // Vérifier qu'il y avait au moins un tiret
  return inner === '' && trimmed.includes('-');
}

/**
 * Détecte si une ligne est une ligne de tableau markdown (| col1 | col2 |)
 */
function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.includes('|', 1);
}

/**
 * Parse les cellules d'une ligne de tableau
 */
function parseTableCells(line: string): string[] {
  const trimmed = line.trim();
  // Retirer les | de début et fin
  const inner = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
  const cleaned = inner.endsWith('|') ? inner.slice(0, -1) : inner;
  return cleaned.split('|').map(cell => cell.trim());
}

/**
 * Détecte le type d'encadré coloré à partir d'une ligne de blockquote
 * Retourne: 'warning' | 'success' | 'error' | 'info' | 'tip' | null
 */
function detectCalloutType(text: string): 'warning' | 'success' | 'error' | 'info' | 'tip' | null {
  const lower = text.toLowerCase();
  if (lower.includes('⚠️') || lower.includes('attention') || lower.includes('calcul incomplet') || lower.includes('avertissement')) return 'warning';
  if (lower.includes('✅') || lower.includes('récupérable') || lower.includes('complet')) return 'success';
  if (lower.includes('❌') || lower.includes('non récupérable') || lower.includes('erreur')) return 'error';
  if (lower.includes('💡') || lower.includes('conseil') || lower.includes('astuce')) return 'tip';
  if (lower.includes('ℹ️') || lower.includes('note') || lower.includes('information')) return 'info';
  return null;
}

/**
 * Retourne les classes CSS pour un type d'encadré
 */
function getCalloutClasses(type: 'warning' | 'success' | 'error' | 'info' | 'tip'): string {
  switch (type) {
    case 'warning': return 'bg-amber-50 border-l-4 border-amber-400 text-amber-800';
    case 'success': return 'bg-emerald-50 border-l-4 border-emerald-400 text-emerald-800';
    case 'error':   return 'bg-red-50 border-l-4 border-red-400 text-red-800';
    case 'tip':     return 'bg-blue-50 border-l-4 border-blue-400 text-blue-800';
    case 'info':    return 'bg-slate-50 border-l-4 border-slate-400 text-slate-700';
  }
}

/**
 * Découpe le contenu Markdown en blocs et retourne des éléments React stylés
 * Supporte : titres h2/h3, listes puces/numérotées, tableaux, blockquotes colorés,
 *            séparateurs, paragraphes, gras, italique, code inline, liens
 */
export function renderMarkdown(content: string): React.ReactNode {
  // Normaliser les sauts de ligne
  const normalized = content
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const lines = normalized.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Ligne vide — skip
    if (trimmed === '') {
      i++;
      continue;
    }

    // --- Séparateur horizontal
    if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
      elements.push(<hr key={key++} className="my-3 border-gray-200" />);
      i++;
      continue;
    }

    // ## Titre H2
    if (trimmed.startsWith('## ')) {
      elements.push(
        <h2 key={key++} className="text-sm font-bold text-gray-900 mt-4 mb-1.5 first:mt-0 uppercase tracking-wide">
          {renderInline(trimmed.slice(3))}
        </h2>
      );
      i++;
      continue;
    }

    // ### Titre H3
    if (trimmed.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="text-sm font-semibold text-gray-700 mt-3 mb-1">
          {renderInline(trimmed.slice(4))}
        </h3>
      );
      i++;
      continue;
    }

    // #### Titre H4
    if (trimmed.startsWith('#### ')) {
      elements.push(
        <h4 key={key++} className="text-sm font-medium text-gray-600 mt-2 mb-0.5">
          {renderInline(trimmed.slice(5))}
        </h4>
      );
      i++;
      continue;
    }

    // ========== TABLEAU MARKDOWN ==========
    if (isTableRow(trimmed)) {
      const tableRows: string[] = [];
      let hasHeader = false;

      // Collecter toutes les lignes du tableau
      while (i < lines.length && (isTableRow(lines[i].trim()) || isTableSeparator(lines[i].trim()))) {
        if (isTableSeparator(lines[i].trim())) {
          hasHeader = true;
        } else {
          tableRows.push(lines[i]);
        }
        i++;
      }

      if (tableRows.length > 0) {
        const headerCells = hasHeader ? parseTableCells(tableRows[0]) : null;
        const bodyRows = hasHeader ? tableRows.slice(1) : tableRows;

        elements.push(
          <div key={key++} className="my-2.5 overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              {headerCells && (
                <thead>
                  <tr className="bg-gray-50">
                    {headerCells.map((cell, ci) => (
                      <th key={ci} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-b border-gray-200">
                        {renderInline(cell)}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {bodyRows.map((row, ri) => {
                  const cells = parseTableCells(row);
                  return (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                      {cells.map((cell, ci) => (
                        <td key={ci} className="px-3 py-1.5 text-sm text-gray-700 border-b border-gray-100">
                          {renderInline(cell)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // ========== LISTE À PUCES (- item ou * item) ==========
    if (/^[-*]\s+/.test(trimmed)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^[-*]\s+/, '');
        items.push(
          <li key={`li${key++}`} className="text-sm leading-relaxed">
            {renderInline(itemText)}
          </li>
        );
        i++;
      }
      elements.push(
        <ul key={key++} className="list-disc pl-4 mb-2 space-y-0.5">{items}</ul>
      );
      continue;
    }

    // ========== LISTE NUMÉROTÉE (1. item) ==========
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^\d+\.\s+/, '');
        items.push(
          <li key={`oli${key++}`} className="text-sm leading-relaxed">
            {renderInline(itemText)}
          </li>
        );
        i++;
      }
      elements.push(
        <ol key={key++} className="list-decimal pl-4 mb-2 space-y-0.5">{items}</ol>
      );
      continue;
    }

    // ========== BLOCKQUOTE / ENCADRÉ COLORÉ ==========
    if (trimmed.startsWith('> ')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().slice(2));
        i++;
      }
      const fullText = quoteLines.join(' ');
      const calloutType = detectCalloutType(fullText);

      if (calloutType) {
        elements.push(
          <div key={key++} className={`${getCalloutClasses(calloutType)} rounded-r-lg px-3 py-2 my-2 text-sm leading-relaxed`}>
            {renderInline(fullText)}
          </div>
        );
      } else {
        elements.push(
          <blockquote key={key++} className="border-l-4 border-gray-300 pl-3 text-gray-600 my-2 text-sm leading-relaxed">
            {renderInline(fullText)}
          </blockquote>
        );
      }
      continue;
    }

    // ========== PARAGRAPHE STANDARD ==========
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().startsWith('## ') &&
      !lines[i].trim().startsWith('### ') &&
      !/^[-*]\s+/.test(lines[i].trim()) &&
      !/^\d+\.\s+/.test(lines[i].trim()) &&
      !/^-{3,}$/.test(lines[i].trim()) &&
      !/^\*{3,}$/.test(lines[i].trim()) &&
      !lines[i].trim().startsWith('> ') &&
      !isTableRow(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }

    if (paraLines.length > 0) {
      elements.push(
        <p key={key++} className="mb-2 last:mb-0 text-sm leading-relaxed">
          {renderInline(paraLines.join(' '))}
        </p>
      );
    }
  }

  // Session 295 : Bandeau placeholders supprimé — faux positifs, règle interdiction dans generationCourrier.ts

  return <>{elements}</>;
}

// ============================================================
// Composant ChatMessage
// ============================================================

interface ChatMessageProps {
  message: ChatMessageType;
  onQuickReply?: (text: string) => void;
}

export default function ChatMessage({ message, onQuickReply }: ChatMessageProps) {
  const { selectAction } = useChat();
  const isUser = message.role === 'user';

  // Session 222 : Détecter les listes numérotées en fin de message SENTINELLE
  // → les transformer en chips cliquables (quick reply)
  // Condition : message SENTINELLE, pas d'actions JSON, contenu se termine par une liste numérotée
  const extractQuickReplies = (): { items: string[]; contentSansListe: string } | null => {
    if (isUser || !onQuickReply || (message.actions && message.actions.length > 0)) return null;

    const lines = message.content.trimEnd().split('\n');
    // Collecter les lignes numérotées depuis la fin
    const listeItems: string[] = [];
    let i = lines.length - 1;
    while (i >= 0) {
      const trimmed = lines[i].trim();
      if (/^\d+\.\s+/.test(trimmed)) {
        // Extraire le texte (sans le numéro) — garder tout y compris "Texte - description"
        const texte = trimmed.replace(/^\d+\.\s+/, '').split(' - ')[0].trim();
        listeItems.unshift(texte);
        i--;
      } else if (trimmed === '') {
        i--;
      } else {
        break;
      }
    }
    if (listeItems.length < 2) return null; // pas de liste ou 1 seul item → pas de chips

    // Contenu sans la liste numérotée
    const contentSansListe = lines.slice(0, i + 1).join('\n').trimEnd();
    return { items: listeItems, contentSansListe };
  };

  const quickReplies = extractQuickReplies();

  const handleActionExecute = (action: ProposedAction) => {
    console.log('Action sélectionnée:', action.type, action.data);
    selectAction(action);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-lg px-4 py-2 ${
          isUser
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-800 border border-gray-200'
        }`}
      >
        {/* Indicateur de rôle */}
        <div
          className={`text-xs font-medium mb-1 ${
            isUser ? 'text-blue-100' : 'text-gray-500'
          }`}
        >
          {isUser ? 'Vous' : 'Assistant'}
        </div>

        {/* Contenu du message */}
        <div className={`text-sm break-words ${isUser ? '' : 'prose prose-sm max-w-none'}`}>
          {isUser ? (
            <span className="whitespace-pre-wrap">{message.content}</span>
          ) : quickReplies ? (
            <>
              {renderMarkdown(quickReplies.contentSansListe)}
              {/* Session 222 : Chips cliquables pour les choix proposés par SENTINELLE */}
              <div className="flex flex-wrap gap-2 mt-3">
                {quickReplies.items.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => onQuickReply!(item)}
                    className="text-xs px-3 py-1.5 rounded-full border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors cursor-pointer font-medium"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </>
          ) : (
            renderMarkdown(message.content)
          )}
        </div>

        {/* Actions proposées par SENTINELLE */}
        {!isUser && message.actions && message.actions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-2 font-medium">
              Actions suggérées :
            </div>
            <div className="flex flex-wrap gap-2">
              {message.actions.map((action) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  onExecute={handleActionExecute}
                />
              ))}
            </div>
          </div>
        )}

        {/* Incitation aux pièces justificatives */}
        {!isUser && message.incitationPieces && (
          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
              <Camera className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{message.incitationPieces}</span>
            </div>
          </div>
        )}

        {/* Horodatage */}
        <div
          className={`text-xs mt-1 ${
            isUser ? 'text-blue-200' : 'text-gray-400'
          }`}
        >
          {message.timestamp.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
}
